import React from 'react';
import { Box, Chip, Tooltip, CircularProgress, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import AdminSurface from '../../../../components/admin/AdminSurface';
import type { GameMissingPlu } from '../../../../types/features/games';
import { adminTableSx } from '../../../../styles/adminComponentStyles';
import { formatGameCurrency } from '../../details/mappers/gameTableMappers';

interface GamesMissingPluTableProps {
  games: GameMissingPlu[];
  loading: boolean;
}

const formatFirstSeen = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const GamesMissingPluTable: React.FC<GamesMissingPluTableProps> = ({ games, loading }) => (
  <AdminSurface sx={{ overflow: 'hidden' }}>
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label="games missing PLU table" sx={adminTableSx}>
        <TableHead>
          <TableRow>
            <TableCell>State</TableCell>
            <TableCell>Game</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>First report</TableCell>
            <TableCell>First seen</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Tickets / pack</TableCell>
            <TableCell>Recommended PLU</TableCell>
            <TableCell>Catalog corrections</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={9}>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={28} /></Box>
            </TableCell></TableRow>
          ) : games.length === 0 ? (
            <TableRow><TableCell colSpan={9}>
              <Typography sx={{ py: 3 }}>No games missing PLU match this search.</Typography>
            </TableCell></TableRow>
          ) : games.map((game) => (
            <TableRow key={game.id} hover>
              <TableCell>{game.state}</TableCell>
              <TableCell>
                <Link href={`/dashboard/games/${encodeURIComponent(game.state)}`}>
                  {game.game_id}
                </Link>
              </TableCell>
              <TableCell>{game.name || '—'}</TableCell>
              <TableCell>{game.first_report_date || ''}</TableCell>
              <TableCell>{formatFirstSeen(game.first_seen_at)}</TableCell>
              <TableCell>{game.cost_per_ticket == null ? '—' : formatGameCurrency(game.cost_per_ticket)}</TableCell>
              <TableCell>{game.tickets_per_pack ?? '—'}</TableCell>
              <TableCell>
                {game.generated_plu ? (
                  <Tooltip title={`${game.plu_generation.formula || 'State formula'}; includes check digit. Verify before saving.`} arrow>
                    <Chip label={`${game.generated_plu} · estimate`} size="small" color="warning" variant="outlined" />
                  </Tooltip>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {game.plu_generation.reason || 'Recommendation unavailable'}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {game.metadata_corrections?.length ? (
                  <Typography variant="body2" color="error">
                    Correct {game.metadata_corrections.map((field) => field === 'cost_per_ticket' ? 'price per ticket' : 'tickets per pack').join(' and ')} in Manage Games.
                  </Typography>
                ) : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </AdminSurface>
);

export default GamesMissingPluTable;
